/**
 * Paywall Screen
 * Displays subscription offerings and handles purchases
 * Uses RevenueCat's built-in paywall UI when available
 */

import React, { useEffect, useState } from 'react';
import { ScrollView, Platform, Linking } from 'react-native';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  ButtonText,
  ButtonSpinner,
  Pressable,
} from '@gluestack-ui/themed';
import { useSubscriptionStore, useHasProAccess, useIsInTrial, useTrialDaysLeft } from '../store/subscriptionStore';
import { getManagementURL, isPurchasesConfigured } from '../services/purchases';
import { useTranslation } from 'react-i18next';
import { FS_XS, FS_SM, FS_MD, FS_LG, FS_XL, FS_2XL, FS_3XL } from '../theme';
import { getPrivacyPolicyUrl, getTermsOfServiceUrl } from '../config/legalUrls';
import { useAppBannerStore } from '../hooks/useAppBanner';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { ScreenHeader } from '../components/ui/ScreenHeader';
import { LoadingState } from '../components/ui/LoadingState';
import { useDisplayTheme } from '../hooks/useDisplayTheme';

interface PaywallScreenProps {
  navigation: { goBack: () => void };
  route?: {
    params?: {
      feature?: string;
    };
  };
}

export function PaywallScreen({ navigation, route }: PaywallScreenProps) {
  const feature = route?.params?.feature;
  const {
    subscription,
    isLoading,
    isPurchasing,
    error,
    presentPaywall,
    restore,
    clearError,
  } = useSubscriptionStore();

  const hasProAccess = useHasProAccess();
  const isInTrial = useIsInTrial();
  const trialDaysLeft = useTrialDaysLeft();
  const { t } = useTranslation();
  const { theme, fontScale, ffs } = useDisplayTheme();

  const showBanner = useAppBannerStore((s) => s.showBanner);
  const [successDialog, setSuccessDialog] = useState<{ title: string; message: string } | null>(null);

  const handlePresentPaywall = async () => {
    clearError();
    const success = await presentPaywall();

    if (success) {
      setSuccessDialog({
        title: t('paywall.successTitle'),
        message: t('paywall.successMessage'),
      });
    }
  };

  // Auto-present RevenueCat paywall on mount if configured and not already subscribed
  // Mount-only: intentionally runs once to auto-present paywall
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (!hasProAccess && !isInTrial && isPurchasesConfigured()) {
      // Small delay to let the screen render first
      const timer = setTimeout(() => {
        handlePresentPaywall();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, []);
  /* eslint-enable react-hooks/exhaustive-deps */

  const handleRestore = async () => {
    clearError();
    const success = await restore();

    if (success) {
      setSuccessDialog({
        title: t('paywall.restoreSuccessTitle'),
        message: t('paywall.restoreSuccessMessage'),
      });
    } else {
      showBanner({ message: t('paywall.restoreNoMessage'), type: 'info' });
    }
  };

  const handleManageSubscription = async () => {
    const url = await getManagementURL();
    if (url) {
      Linking.openURL(url);
    } else {
      // Fallback to platform subscription settings
      if (Platform.OS === 'ios') {
        Linking.openURL('itms-apps://apps.apple.com/account/subscriptions');
      } else if (Platform.OS === 'android') {
        Linking.openURL('https://play.google.com/store/account/subscriptions');
      }
    }
  };

  // If user already has pro access, show subscription status
  if (hasProAccess && !isInTrial) {
    return (
      <ScrollView testID="paywall-screen" style={{ flex: 1, backgroundColor: theme.background }}>
        <ScreenHeader title="Fluencia Pro" />
        <Box p="$5">
          <Box style={{ backgroundColor: theme.card }} p="$6" rounded="$xl" hardShadow="2" mb="$5">
            <VStack space="md" alignItems="center">
              <Box
                w="$16"
                h="$16"
                rounded="$full"
                style={{ backgroundColor: theme.success }}
                justifyContent="center"
                alignItems="center"
              >
                <Text allowFontScaling={false} style={{ fontSize: FS_3XL * fontScale, color: 'white', ...ffs }}>✓</Text>
              </Box>
              <Text allowFontScaling={false} accessibilityRole="header" style={{ fontSize: FS_XL * fontScale, fontWeight: 'bold', color: theme.text, textAlign: 'center', ...ffs }}>
                {t('paywall.activeTitle')}
              </Text>
              <Text allowFontScaling={false} style={{ fontSize: FS_MD * fontScale, color: theme.textSecondary, textAlign: 'center', ...ffs }}>
                {t('paywall.activeMessage')}
              </Text>
              {subscription?.expiresAt && (
                <Text allowFontScaling={false} style={{ fontSize: FS_SM * fontScale, color: theme.textSecondary, ...ffs }}>
                  {t('paywall.renews')} {new Date(subscription.expiresAt).toLocaleDateString()}
                </Text>
              )}
            </VStack>
          </Box>

          <Button
            testID="paywall-button-manage"
            accessibilityLabel={t('paywall.manageSubscription')}
            size="md"
            variant="outline"
            style={{ borderColor: theme.primary }}
            onPress={handleManageSubscription}
          >
            <ButtonText allowFontScaling={false} style={{ fontSize: FS_MD * fontScale, color: theme.primary, ...ffs }}>{t('paywall.manageSubscription')}</ButtonText>
          </Button>
        </Box>
      </ScrollView>
    );
  }

  const benefits = [
    t('paywall.unlimitedPronunciation'),
    t('paywall.aiConversationPractice'),
    t('paywall.accessAllLessons'),
    t('paywall.detailedFeedback'),
    t('paywall.progressTracking'),
    t('paywall.prioritySupport'),
  ];

  const confirmDialog = successDialog ? (
    <ConfirmDialog
      visible={!!successDialog}
      title={successDialog.title}
      message={successDialog.message}
      confirmLabel={t('common.continue')}
      confirmOnly
      onConfirm={() => {
        setSuccessDialog(null);
        navigation.goBack();
      }}
      onCancel={() => setSuccessDialog(null)}
    />
  ) : null;

  return (
    <>
    <ScrollView testID="paywall-screen" accessibilityLabel="Paywall" style={{ flex: 1, backgroundColor: theme.background }}>
      <ScreenHeader title="Fluencia Pro" />
      <Box p="$5">
        {/* Header */}
        <VStack space="sm" mb="$6" alignItems="center">
          <Text allowFontScaling={false} accessibilityRole="header" style={{ fontSize: FS_2XL * fontScale, fontWeight: 'bold', color: theme.text, textAlign: 'center', ...ffs }}>
            {t('paywall.unlockTitle')}
          </Text>
          {feature && (
            <Text allowFontScaling={false} style={{ fontSize: FS_MD * fontScale, color: theme.primary, fontWeight: '600', textAlign: 'center', ...ffs }}>
              {t('paywall.upgradeToAccess')} {feature}
            </Text>
          )}
          {isInTrial && trialDaysLeft !== null && (
            <Box style={{ backgroundColor: theme.warning }} px="$4" py="$2" rounded="$full">
              <Text allowFontScaling={false} style={{ fontSize: FS_MD * fontScale, color: 'white', fontWeight: '600', ...ffs }}>
                {t('paywall.daysLeftInTrial', { count: trialDaysLeft })}
              </Text>
            </Box>
          )}
        </VStack>

        {/* Benefits */}
        <Box style={{ backgroundColor: theme.card }} p="$5" rounded="$xl" hardShadow="2" mb="$5">
          <Text allowFontScaling={false} accessibilityRole="header" mb="$4" style={{ fontSize: FS_LG * fontScale, fontWeight: 'bold', color: theme.text, ...ffs }}>
            {t('paywall.proBenefitsTitle')}
          </Text>
          <VStack space="md">
            {benefits.map((benefit, index) => (
              <HStack key={index} space="sm" alignItems="center">
                <Box
                  w="$6"
                  h="$6"
                  rounded="$full"
                  style={{ backgroundColor: theme.success }}
                  justifyContent="center"
                  alignItems="center"
                >
                  <Text allowFontScaling={false} style={{ fontSize: FS_XS * fontScale, color: 'white', ...ffs }}>✓</Text>
                </Box>
                <Text allowFontScaling={false} style={{ fontSize: FS_MD * fontScale, color: theme.text, ...ffs }}>{benefit}</Text>
              </HStack>
            ))}
          </VStack>
        </Box>

        {/* Loading state */}
        {(isLoading || isPurchasing) && (
          <LoadingState
            message={isPurchasing ? t('common.processing') : t('common.loading')}
            testID="paywall-loading"
          />
        )}

        {/* Error message */}
        {error && (
          <Box style={{ backgroundColor: theme.error }} p="$3" rounded="$md" mb="$4">
            <Text allowFontScaling={false} style={{ fontSize: FS_MD * fontScale, color: 'white', ...ffs }}>{error}</Text>
          </Box>
        )}

        {/* Subscribe button - opens RevenueCat paywall */}
        <Button
          testID="paywall-button-subscribe"
          accessibilityLabel={t('paywall.viewSubscriptionOptions')}
          size="lg"
          style={{ backgroundColor: theme.primary }}
          onPress={handlePresentPaywall}
          isDisabled={isPurchasing || isLoading}
          mb="$3"
        >
          {isPurchasing ? (
            <ButtonSpinner color="white" />
          ) : (
            <ButtonText allowFontScaling={false} style={{ fontSize: FS_LG * fontScale, color: 'white', ...ffs }}>{t('paywall.viewSubscriptionOptions')}</ButtonText>
          )}
        </Button>

        {/* Restore purchases */}
        <Button
          testID="paywall-button-restore"
          accessibilityLabel={t('paywall.restorePurchases')}
          size="md"
          variant="link"
          onPress={handleRestore}
          isDisabled={isLoading || isPurchasing}
        >
          <ButtonText allowFontScaling={false} style={{ fontSize: FS_MD * fontScale, color: theme.primary, ...ffs }}>{t('paywall.restorePurchases')}</ButtonText>
        </Button>

        {/* Terms */}
        <VStack space="xs" mt="$5" alignItems="center">
          <Text allowFontScaling={false} style={{ fontSize: FS_XS * fontScale, color: theme.textSecondary, textAlign: 'center', ...ffs }}>
            {t('paywall.paymentCharged')} {Platform.OS === 'ios' ? t('paywall.appleId') : Platform.OS === 'android' ? t('paywall.googlePlay') : t('paywall.paymentMethod')}.
          </Text>
          <Text allowFontScaling={false} style={{ fontSize: FS_XS * fontScale, color: theme.textSecondary, textAlign: 'center', ...ffs }}>
            {t('paywall.autoRenewDisclaimer')}
          </Text>
          <HStack space="md" mt="$2">
            <Pressable testID="paywall-link-terms" accessibilityLabel="Terms of service" accessibilityRole="link" onPress={() => Linking.openURL(getTermsOfServiceUrl())}>
              <Text allowFontScaling={false} style={{ fontSize: FS_XS * fontScale, color: theme.primary, ...ffs }}>{t('paywall.termsOfService')}</Text>
            </Pressable>
            <Pressable testID="paywall-link-privacy" accessibilityLabel="Privacy policy" accessibilityRole="link" onPress={() => Linking.openURL(getPrivacyPolicyUrl())}>
              <Text allowFontScaling={false} style={{ fontSize: FS_XS * fontScale, color: theme.primary, ...ffs }}>{t('paywall.privacyPolicy')}</Text>
            </Pressable>
          </HStack>
        </VStack>
      </Box>
    </ScrollView>
    {confirmDialog}
    </>
  );
}
